/**
 * Payment Service
 * Handles all payment-related API calls
 */

/**
 * Initiate EFT payment - gets payment reference
 * @param {string} referenceId - The payment reference ID
 * @returns {Promise<Object>} Updated reference with payment_reference
 */
export function initiateEftPayment(referenceId) {
  return axios.get(`${API}eft?ref=${referenceId}`)
    .then(({ data }) => data)
}

/**
 * Create Kazang mobile money transaction
 * @param {Object} reference - Reference object with amount and debtor_name
 * @param {string} referenceId - The payment reference ID
 * @returns {Promise<Object>} Kazang transaction response with redirect ID
 */
export function createKazangTransaction(reference, referenceId) {
  const payload = {
    API_KEY: '945ac88d-e88a-11ed-91dd-00163e8d4996',
    amount: reference.amount,
    Service_Description: `Chrilan Technology Subscription ( ${reference.debtor_name} )`,
    Client_Refrence: referenceId,
    Cancel_Url: 'https://gatewaykazang.chrilantech.com/test-failed',
    Sucess_Url: 'https://subscription.chrilantech.com/'
  }

  return axios.post('https://lna.pos.co.zm/KazangPaymentGateway/GateWay/CreateTransaction', payload)
    .then(({ data }) => {
      if (data.Error) {
        throw new Error(data.Error || 'Failed to create Kazang transaction')
      }
      return data
    })
}

/**
 * Get Kazang gateway redirect URL
 * @param {string} transactionId - Kazang transaction ID
 * @returns {string} Full redirect URL
 */
export function getKazangRedirectUrl(transactionId) {
  return `https://gatewaykazang.chrilantech.com/kazangOnline?id=${transactionId}`
}

/**
 * Fetch Kazang VCD accounts for a debtor
 * @param {string} debtorId - The debtor ID
 * @returns {Promise<Array>} List of Kazang accounts
 */
export function getKazangAccounts(debtorId) {
  return axios.get(`${API}adminCustomerPanel/getKazangIDsByDebtor?debtorID=${debtorId}`)
    .then(({ data }) => data)
}

/**
 * Process VCD payment
 * @param {string} referenceId - The payment reference ID
 * @param {string} kazangId - Kazang account ID
 * @param {string} storeDb - Store database identifier
 * @returns {Promise<void>}
 */
export function processVcdPayment(referenceId, kazangId, storeDb) {
  return axios.get(`${API}paymentCompleted/enterpriseVCD/${referenceId}?kazangID=${kazangId}&storeDB=${storeDb}`)
}

/**
 * Process manual payment (e.g., after returning from Kazang gateway)
 * @param {Object} payload - Payment payload
 * @param {string} payload.id - Reference ID
 * @param {string} payload.payment_reference - Kazang payment reference
 * @param {string} payload.payment_type - Payment type (e.g., 'Kazang')
 * @returns {Promise<void>}
 */
export function processManualPayment(payload) {
  return axios.post(`${API}paymentCompleted/processManualPayment`, payload)
}

/**
 * Create DPO payment session
 * @param {string} referenceId - The payment reference ID
 * @returns {Promise<Object>} DPO payment response with paymentUrl
 */
export function createDpoPayment(referenceId) {
  return axios.post(`${API}dpo/create-payment`, { referenceId })
    .then(({ data }) => {
      // Parse JSON string if needed
      if (typeof data === 'string') {
        return JSON.parse(data)
      }
      return data
    })
}

/**
 * Get DPO discount information for an amount
 * @param {number} amount - The original amount
 * @returns {Promise<Object>} Discount info
 */
export function getDpoDiscountInfo(amount) {
  return axios.get(`${API}dpo/discount-info?amount=${amount}`)
    .then(({ data }) => {
      if (typeof data === 'string') {
        return JSON.parse(data)
      }
      return data
    })
}

/**
 * Verify DPO payment status
 * @param {string} sessionToken - DPO session token
 * @returns {Promise<Object>} Payment verification response
 */
export function verifyDpoPayment(sessionToken) {
  return axios.get(`${API}dpo/verify/${sessionToken}`)
    .then(({ data }) => {
      if (typeof data === 'string') {
        return JSON.parse(data)
      }
      return data
    })
}

export default {
  initiateEftPayment,
  createKazangTransaction,
  getKazangRedirectUrl,
  getKazangAccounts,
  processVcdPayment,
  processManualPayment,
  createDpoPayment,
  getDpoDiscountInfo,
  verifyDpoPayment
}
